<script setup lang="ts">
import { computed, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { ITrackersModalPageCommonProps } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/types';
import type { IChangeHistoryEntry } from '@modules/change-history/components/VChangeHistory/types';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VChangeHistory from '@modules/change-history/components/VChangeHistory/VChangeHistory.vue';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { useMerchantIdStore } from '@modules/merchant/store';
import { useQueryClient } from '@tanstack/vue-query';
import { assert, isArrayOf, isDate, isString } from '@utils/guards';

import type { IFixedTrackerPaymentsComposableOptions, IFixedTrackerPaymentsForm } from '../../types';
import { useFixedTrackerPaymentsForm } from '../../composables';
import { AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS, TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS } from '../../constants';
import { useDeleteFixedTrackerPaymentsMutation, useUpdateFixedTrackerPaymentsMutation } from '../../mutations';
import { useShowFixedPaymentQuery } from '../../queries';
import FixedPaymentsHistoryEditPage from '../../submodules/fixed-payments-history-edit/pages/FixedPaymentsHistoryEditPage/FixedPaymentsHistoryEditPage.vue';

const props = defineProps<ITrackersModalPageCommonProps>();

const queryClient = useQueryClient();
const route = useRoute();
const router = useRouter();
const merchantId = toRef(useMerchantIdStore(), 'merchantId');
const { formatMonthDate } = useFormatMonthDate();

const affiliateId = computed(() => (isString(route.params.id) ? Number(route.params.id) : 0));
const trackerId = computed(() => (isString(route.params.trackerId) ? Number(route.params.trackerId) : 0));
const itemId = computed(() => (isString(route.params.itemId) ? Number(route.params.itemId) : 0));

const { data, isLoading, setData } = useShowFixedPaymentQuery(affiliateId, trackerId);

const {
  mutateAsync: updateFixedPayments,
  data: updateHistoryResponse,
  isLoading: isUpdateLoading,
  errors,
  clearError,
} = useUpdateFixedTrackerPaymentsMutation();

const { mutateAsync: removeHistoryFixedPayment } = useDeleteFixedTrackerPaymentsMutation();

const formOptions = computed<IFixedTrackerPaymentsComposableOptions>(() => ({
  serialIdValue: data.value?.tracker?.tracker_code,
  merchantNameValue: data.value?.tracker.merchant_name,
  datePickerSelectionMode: 'multiple',
}));

const { formSchema, formGroups } = useFixedTrackerPaymentsForm(formOptions);

const historyOfFixedPaymentsChanges = computed<IChangeHistoryEntry[] | undefined>(() => data.value?.tracker.history_of_fixed_payments_changes?.map((item) => ({
  ...item,
  active: itemId.value === item.id,
})));

const selectedHistoryItem = computed<IChangeHistoryEntry | undefined>(() => historyOfFixedPaymentsChanges.value?.find((item) => item.active));

async function onSubmit({ payment_size, dates, serial_id, description }: IFixedTrackerPaymentsForm): Promise<void> {
  try {
    assert(isArrayOf(isDate, dates));
    await updateFixedPayments({
      affiliateId: affiliateId.value,
      trackerId: trackerId.value,
      history_of_fixed_payments_change: {
        id: trackerId.value,
        tracker_code: serial_id,
        payment_size,
        description,
        dates: formatMonthDate(dates),
      },
    });

    updateHistoryOfFixedPaymentsChanges(updateHistoryResponse.value?.history_of_fixed_payments_changes);

    if (props.trackersListQueryKey) {
      await queryClient.invalidateQueries({ queryKey: [props.trackersListQueryKey] });
    }
  } catch {}
}

function onHistoryEdit(entry: IChangeHistoryEntry): void {
  void router.push({ name: props.editHistoryRoute.name, params: { merchantId: merchantId.value, itemId: entry.id } });
}

function updateHistoryOfFixedPaymentsChanges(list?: IChangeHistoryEntry[]): void {
  setData((queryData) => {
    if (!queryData) {
      return queryData;
    }

    return {
      ...queryData,
      tracker: {
        ...queryData.tracker,
        history_of_fixed_payments_changes: list ?? queryData.tracker.history_of_fixed_payments_changes,
      },
    };
  });
}

async function onHistoryDelete(entry: IChangeHistoryEntry): Promise<void> {
  try {
    await removeHistoryFixedPayment(entry.id);

    updateHistoryOfFixedPaymentsChanges(historyOfFixedPaymentsChanges.value?.filter(({ id }) => id !== entry.id));

    if (itemId.value === entry.id) {
      await router.replace(props.backFromEditHistoryRoute);
    }
  } catch {}
}
</script>

<template>
  <VCard v-data-qa-auto-id="'FixedTrackerPaymentsPage'"
    type="form"
    class="rounded-b-none"
    :title="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS, 'HEADER')"
  >
    <VForm
      :schema="formSchema"
      :groups="formGroups"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_FIXED_PAYMENTS, 'SUBMIT_BUTTON')"
      :custom-errors="errors"
      :is-loading="isUpdateLoading"
      @submit="onSubmit"
      @interacted="clearError"
    />
  </VCard>

  <div class="rounded-b-xl border-t bg-content-color">
    <VLoader :loading="isLoading">
      <VChangeHistory
        class="rounded-t-none"
        :list="historyOfFixedPaymentsChanges"
        :edit-permission="AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS.HISTORY_CHANGES_EDIT"
        :delete-permission="AFFILIATES_APPROVED_FIXED_PAYMENTS_PERMISSIONS.HISTORY_CHANGES_DELETE"
        @edit="onHistoryEdit"
        @delete="onHistoryDelete"
      >
        <FixedPaymentsHistoryEditPage
          :back-from-edit-history-route="backFromEditHistoryRoute"
          :merchant-name="selectedHistoryItem?.merchant_name"
        />
      </VChangeHistory>
    </VLoader>
  </div>
</template>
